import { Link } from "@inertiajs/react"
import { Bookmark, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { crewSegmentsPath } from "@/routes"

/**
 * OKS-28 — Segment selector for the Crew Roster page.
 *
 * Sits ABOVE the OKS-25 filter bar (UX Spec §2.1). shadcn `Select` —
 * NOT pills — chosen for the 50-segment cap (PRD §Limit). Pills cap
 * usable density at ~6; the dropdown scales clean to 50.
 *
 * **Items (in DOM order):**
 *   1. `All crew` (default; clears any applied segment)
 *   2. divider
 *   3. The brand's segments, alpha-sorted by name. Each item shows
 *      the segment name + a muted member count to the right (visually
 *      hidden `aria-label="Showing N members"` for SR parity per
 *      UX Spec §6).
 *   4. divider
 *   5. `Manage segments…` — Inertia `<Link>` to `crewSegmentsPath()`.
 *      Closes the popover via shadcn defaults.
 *
 * **Locked testid:** `crew-list-segment-select` (UX Spec §2.1 / QA-Plan).
 *
 * **Wire-key contract:** `segments[i].members_count` (plural-with-s).
 *
 * **Keyboard nav** (UX Spec §6 + shadcn defaults): ↑/↓ traverse, Enter
 * applies, Esc closes. `aria-label` on the trigger announces the
 * combobox role.
 */
export interface SegmentSelectorProps {
  /** Brand's segments — selector items, alpha-sorted. */
  segments: { id: number; name: string; members_count: number }[]
  /** Currently-applied segment, or null when "All crew" is selected. */
  appliedSegment: { id: number; name: string } | null
  /**
   * Apply or clear a segment. `null` reverts to "All crew" (also
   * clears any ad-hoc filter rules per UX Spec §1).
   */
  onSegmentChange: (id: number | null) => void
}

const ALL_CREW_VALUE = "__all_crew__"
const MANAGE_VALUE = "__manage_segments__"

export function SegmentSelector({
  segments,
  appliedSegment,
  onSegmentChange,
}: SegmentSelectorProps) {
  const { t } = useTranslation()

  const value = appliedSegment ? String(appliedSegment.id) : ALL_CREW_VALUE

  const handleValueChange = (next: string) => {
    if (next === ALL_CREW_VALUE) {
      onSegmentChange(null)
      return
    }
    if (next === MANAGE_VALUE) {
      // Manage entry is rendered as an Inertia <Link> wrapper around
      // the SelectItem, so the click goes through the link-handler;
      // this branch is reachable only via keyboard "Enter" on the
      // item, which we ignore here (the embedded <Link> click
      // handler navigates instead).
      return
    }
    const id = Number.parseInt(next, 10)
    if (!Number.isNaN(id)) onSegmentChange(id)
  }

  return (
    <Select
      items={[
        { value: ALL_CREW_VALUE, label: t("crew_segments.selector.all_crew") },
        ...segments.map((segment) => ({
          value: String(segment.id),
          label: segment.name,
        })),
      ]}
      value={value}
      onValueChange={(next) => next != null && handleValueChange(next)}
    >
      <SelectTrigger
        data-testid="crew-list-segment-select"
        aria-label={t("crew_segments.selector.label")}
        className="w-full sm:max-w-sm"
      >
        <div className="flex items-center gap-2">
          {appliedSegment && (
            <Bookmark className="size-3.5 shrink-0" aria-hidden="true" />
          )}
          <SelectValue placeholder={t("crew_segments.selector.placeholder")} />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          value={ALL_CREW_VALUE}
          data-testid="crew-list-segment-option-all"
        >
          {t("crew_segments.selector.all_crew")}
        </SelectItem>
        {segments.length > 0 && <SelectSeparator />}
        {segments.map((segment) => (
          <SelectItem
            key={segment.id}
            value={String(segment.id)}
            data-testid={`crew-list-segment-option-${segment.id}`}
          >
            <div className="flex w-full items-center justify-between gap-3">
              <span className="truncate">{segment.name}</span>
              <span
                className="text-muted-foreground shrink-0 text-xs tabular-nums"
                aria-label={t("crew_segments.selector.member_count_aria", {
                  count: segment.members_count,
                })}
              >
                {t("crew_segments.selector.member_count", {
                  count: segment.members_count,
                })}
              </span>
            </div>
          </SelectItem>
        ))}
        <SelectSeparator />
        {/*
          Manage entry — Inertia `<Link>` so middle-click + meta-click
          + keyboard Enter all open the manage page. Wrapping a
          `SelectItem` keeps the dropdown styling consistent. The
          `value` here is unused (the click navigates before
          onValueChange fires).
        */}
        <Link
          href={crewSegmentsPath()}
          data-testid="crew-list-segment-option-manage"
          className="hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center justify-between gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none"
        >
          <span>{t("crew_segments.selector.manage_segments")}</span>
          <ChevronRight
            className="text-muted-foreground size-4"
            aria-hidden="true"
          />
        </Link>
      </SelectContent>
    </Select>
  )
}
